import { MASCARA_CODIGO } from "@vetbolso/core";
import { Logo, mascarar } from "@vetbolso/ui";
import { useState } from "react";
import { entrarComCodigo, pedirCodigo } from "./api.ts";
import { BotaoCheckout } from "./checkout.tsx";
import { useApp } from "./estado.tsx";
import { FormCpf } from "./telas/conta.tsx";

const CHECKOUT = import.meta.env.VITE_CHECKOUT_URL as string | undefined;

function Moldura({ titulo, sub, children }: { titulo: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className="gate">
      <div className="gate-in">
        <span className="lockup">
          <Logo tamanho={40} />
          <span className="word">
            Vet<b>Bolso</b>
          </span>
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <h1>{titulo}</h1>
          {sub ? <p className="sub">{sub}</p> : null}
        </div>
        {children}
      </div>
    </div>
  );
}

const msg = (e: unknown) => (e instanceof Error ? e.message : "algo deu errado");

/** Entrada sem senha: código de 6 dígitos por e-mail; a conta nasce no primeiro acesso. */
export function TelaEntrar() {
  const { recarregar } = useApp();
  const [email, setEmail] = useState("");
  const [enviado, setEnviado] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const agir = async (f: () => Promise<void>) => {
    setOcupado(true);
    setErro(null);
    try {
      await f();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <Moldura titulo="A consulta veterinária no bolso" sub="Doses, tóxicos, carência e receita, na hora e sem internet.">
      {!enviado ? (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            agir(async () => {
              await pedirCodigo(email.trim().toLowerCase());
              setEnviado(true);
            });
          }}
        >
          <h3>Entrar</h3>
          <div className="field">
            <label htmlFor="email">E-mail</label>
            <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {erro ? <p className="erro">{erro}</p> : null}
          <button type="submit" className="btn" disabled={ocupado}>
            {ocupado ? "Enviando…" : "Receber código por e-mail"}
          </button>
        </form>
      ) : (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            agir(async () => {
              await entrarComCodigo(email.trim().toLowerCase(), codigo.replace(/\D/g, ""));
              await recarregar();
            });
          }}
        >
          <h3>Digite o código</h3>
          <p className="sub">Enviamos 6 dígitos para {email}. Vale por 10 minutos.</p>
          <div className="field">
            <label htmlFor="otp">Código</label>
            <input
              id="otp"
              className="num otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              required
              pattern="\d{6}"
              title="6 dígitos"
              value={codigo}
              onChange={(e) => setCodigo(mascarar(e, MASCARA_CODIGO))}
            />
          </div>
          {erro ? <p className="erro">{erro}</p> : null}
          <button type="submit" className="btn" disabled={ocupado}>
            {ocupado ? "Entrando…" : "Entrar"}
          </button>
          <button type="button" className="link" onClick={() => setEnviado(false)}>
            Usar outro e-mail
          </button>
        </form>
      )}
    </Moldura>
  );
}

export function TelaSemAcesso() {
  const { estado, recarregar, sairDaConta } = useApp();
  const email = estado.fase === "sem_acesso" ? estado.sessao.usuario.email : "";
  return (
    <Moldura titulo="Sua conta ainda não tem acesso" sub={`Entrou como ${email}. O conteúdo libera quando a compra é confirmada.`}>
      {CHECKOUT ? (
        <BotaoCheckout url={CHECKOUT} produto="VetBolso">
          Assinar o VetBolso
        </BotaoCheckout>
      ) : null}
      <FormCpf aoVincular={() => recarregar()} />
      <button type="button" className="btn ghost" onClick={() => recarregar()}>
        Já paguei, verificar de novo
      </button>
      <button type="button" className="link" onClick={() => sairDaConta()}>
        Sair e entrar com outro e-mail
      </button>
    </Moldura>
  );
}

export function TelaPortao() {
  const { estado, recarregar, sincronizando } = useApp();
  if (estado.fase === "carregando")
    return (
      <Moldura titulo="Carregando a base…">
        <div className="spin" role="status" aria-label="Carregando" />
      </Moldura>
    );
  if (estado.fase === "entrar") return <TelaEntrar />;
  if (estado.fase === "sem_acesso") return <TelaSemAcesso />;
  const textos = {
    sem_release: ["Base em preparação", "Ainda não há uma versão do conteúdo publicada. Tente de novo mais tarde."],
    expirada:
      estado.fase === "expirada" && estado.motivo === "relogio"
        ? ["Confira a data do aparelho", "O relógio está antes do último uso. Ajuste a data e a hora e conecte-se para continuar."]
        : [
            "Conecte-se para continuar",
            "A base salva vale por 7 dias desde a última confirmação do acesso. Conecte-se à internet para renovar.",
          ],
    erro: ["Não foi possível abrir a base", estado.fase === "erro" ? estado.msg : ""],
  } as const;
  const [t, s] = textos[estado.fase as keyof typeof textos] ?? ["", ""];
  return (
    <Moldura titulo={t} sub={s}>
      <button type="button" className="btn" disabled={sincronizando} onClick={() => recarregar()}>
        {sincronizando ? "Tentando…" : "Tentar de novo"}
      </button>
    </Moldura>
  );
}
